<template>
  <div class="bg-gray-50 rounded px-2 py-1 text-xs">
    <!-- Call entries -->
    <div v-if="metadata.call_entries && metadata.call_entries.length > 0">
      <div v-for="(entry, entryIndex) in metadata.call_entries"
           :key="`call-${workItem.id}-${entryIndex}`"
           :class="{'mt-2 pt-2 border-t border-gray-200': entryIndex > 0}">

        <!-- Call Outcome -->
        <div v-if="entry.call_outcome" class="flex items-start gap-1">
          <span class="text-gray-600 shrink-0">Outcome:</span>
          <span class="font-medium" :class="getCallOutcomeClass(entry.call_outcome)">
            {{ entry.call_outcome }}
          </span>
        </div>

        <!-- Medication Category -->
        <div v-if="entry.medication_category" class="flex items-center gap-1 mt-0.5">
          <FontAwesomeIcon :icon="['fas','pills']" class="text-xs text-gray-400" />
          <span class="text-gray-600">{{ entry.medication_category }}</span>
        </div>

        <!-- Call Notes -->
        <div v-if="entry.call_note" class="mt-0.5 text-gray-600 max-h-16 overflow-y-auto">
          <span class="text-gray-500">Note:</span> {{ entry.call_note }}
        </div>
      </div>
    </div>

    <!-- Generic outcome -->
    <div v-else-if="metadata.outcome" class="flex items-center gap-1">
      <span class="text-gray-600">Outcome:</span>
      <span class="font-medium" :class="getOutcomeClass(metadata.outcome)">
        {{ formatOutcome(metadata.outcome) }}
      </span>
    </div>

    <!-- Notes -->
    <div v-if="metadata.notes" class="mt-0.5 text-gray-600 line-clamp-2">
      {{ metadata.notes }}
    </div>

    <!-- Additional notes -->
    <div v-if="metadata.additional_notes" class="mt-0.5 text-gray-600 line-clamp-2">
      <span class="text-gray-500">Additional:</span> {{ metadata.additional_notes }}
    </div>

    <!-- Duration -->
    <div v-if="metadata.duration" class="flex items-center gap-1 mt-0.5">
      <FontAwesomeIcon :icon="['fas','clock']" class="text-xs text-gray-400" />
      <span class="text-gray-600">{{ metadata.duration }} min</span>
    </div>

    <!-- Method -->
    <div v-if="metadata.method" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 mt-0.5">
      {{ metadata.method }}
    </div>

    <!-- Survey responses -->
    <div v-if="isSurveyResponse" class="mt-1">
      <div class="font-medium text-gray-700 mb-1">Survey Response:</div>
      <div class="space-y-1">
        <div v-for="(value, key) in getSurveyResponses()" :key="key" class="flex gap-1">
          <span class="text-gray-500">{{ formatFieldName(key) }}:</span>
          <span class="text-gray-700">{{ formatFieldValue(value) }}</span>
        </div>
      </div>
    </div>

    <!-- Uploaded Images Section -->
    <div v-if="(metadata.uploaded_images && metadata.uploaded_images.length > 0) || allowEdit"
         class="mt-2 pt-2 border-t border-gray-200">
      <div class="text-xs text-gray-500 font-medium mb-1">Attachments:</div>

      <!-- Image Uploader - only show if user can edit -->
      <div v-if="allowEdit" class="mb-1">
        <ImageUploader
          :related-object-id="workItem.id"
          :related-object-content-type-model="'work'"
          :disabled="uploadingImage"
          @image-uploaded="addImage"
        />
      </div>

      <!-- Display uploaded images -->
      <div v-if="metadata.uploaded_images && metadata.uploaded_images.length > 0">
        <div v-for="image in metadata.uploaded_images"
             :key="image.key"
             class="flex items-center gap-1 mb-1">
          <MediaViewer
            :can-delete="allowEdit && !uploadingImage"
            :media-key="image.key"
            @media-deleted="removeImage"
          />
        </div>
      </div>
    </div>

    <!-- More details indicator -->
    <div v-if="hasMoreFields" class="mt-1 text-xs text-primary-600 flex items-center gap-1">
      <FontAwesomeIcon :icon="['fas', 'circle-info']" class="text-xs" />
      <span>Click to view all details</span>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import MediaViewer from '@/components/widgetcomponents/MediaViewer.vue';
import ImageUploader from '@/components/widgetcomponents/MediaUploader.vue';
import axios from 'axios';

export default {
  name: 'GenericWorkDisplay',
  components: {
    FontAwesomeIcon,
    MediaViewer,
    ImageUploader
  },
  props: {
    workItem: {
      type: Object,
      required: true
    },
    metadata: {
      type: Object,
      default: () => ({})
    },
    compact: {
      type: Boolean,
      default: true
    },
    currentUser: {
      type: Object,
      default: null
    }
  },
  emits: ['updated'],
  data() {
    return {
      uploadingImage: false
    };
  },
  computed: {
    isSurveyResponse() {
      return this.metadata.schema_type === 'survey' && this.metadata._survey_metadata;
    },

    allowEdit() {
      return this.workItem.appuser === this.currentUser?.id;
    },

    hasMoreFields() {
      if (!this.metadata) return false;

      const shownFields = [
        'call_entries', 'outcome', 'notes', 'additional_notes',
        'duration', 'method', 'timestamp', 'task_metadata', 'visible_fields',
        'schema_type', '_survey_metadata', 'uploaded_images'
      ];

      const otherFields = Object.keys(this.metadata).filter(key => {
        return !shownFields.includes(key) &&
               this.metadata[key] !== null &&
               this.metadata[key] !== undefined &&
               this.metadata[key] !== '';
      });

      return otherFields.length > 0;
    }
  },
  methods: {
    getCallOutcomeClass(outcome) {
      const lowerOutcome = outcome?.toLowerCase() || '';

      if (lowerOutcome.includes('picked up') ||
          lowerOutcome.includes('successful') ||
          lowerOutcome.includes('completed') ||
          lowerOutcome.includes('confirmed')) {
        return 'text-green-700';
      }

      if (lowerOutcome.includes('refused') ||
          lowerOutcome.includes('declined') ||
          lowerOutcome.includes('unable') ||
          lowerOutcome.includes('no answer')) {
        return 'text-red-700';
      }

      if (lowerOutcome.includes('voicemail') ||
          lowerOutcome.includes('left message') ||
          lowerOutcome.includes('pending') ||
          lowerOutcome.includes('scheduled')) {
        return 'text-yellow-700';
      }

      if (lowerOutcome.includes('spoke with') ||
          lowerOutcome.includes('discussed')) {
        return 'text-blue-700';
      }

      return 'text-gray-700';
    },

    getOutcomeClass(outcome) {
      const lowerOutcome = outcome?.toLowerCase() || '';

      if (lowerOutcome.includes('success') || lowerOutcome.includes('complete')) {
        return 'text-green-700';
      }
      if (lowerOutcome.includes('fail') || lowerOutcome.includes('unable')) {
        return 'text-red-700';
      }
      if (lowerOutcome.includes('pending') || lowerOutcome.includes('in progress')) {
        return 'text-yellow-700';
      }

      return 'text-gray-700';
    },

    formatOutcome(outcome) {
      return outcome.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },

    getSurveyResponses() {
      if (!this.isSurveyResponse) return {};

      const excludeKeys = ['_survey_metadata', 'schema_type', 'timestamp', 'task_metadata'];
      const responses = {};

      Object.keys(this.metadata).forEach(key => {
        if (!excludeKeys.includes(key) && this.metadata[key] !== null && this.metadata[key] !== undefined) {
          responses[key] = this.metadata[key];
        }
      });

      // Only show first 3 responses in compact mode
      if (this.compact && Object.keys(responses).length > 3) {
        const keys = Object.keys(responses).slice(0, 3);
        const compactResponses = {};
        keys.forEach(key => {
          compactResponses[key] = responses[key];
        });
        return compactResponses;
      }

      return responses;
    },

    formatFieldName(fieldName) {
      // Convert snake_case or camelCase to Title Case
      return fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .trim();
    },

    formatFieldValue(value) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (value === null || value === undefined) {
        return 'N/A';
      }
      return String(value);
    },

    async addImage(imageData) {
      this.uploadingImage = true;

      try {
        const updatedImages = [...(this.metadata.uploaded_images || [])];

        const newImage = {
          key: imageData.key,
          url: imageData.url,
          filename: imageData.filename,
          uploaded_by: {
            appuser_id: this.currentUser?.id,
            appuser_name: this.currentUser ? `${this.currentUser.last_name}, ${this.currentUser.first_name}` : 'Unknown User',
            created_at: new Date().toISOString()
          }
        };

        updatedImages.push(newImage);

        await axios.patch(`/api/work/${this.workItem.id}/`, {
          metadata: {
            ...this.metadata,
            uploaded_images: updatedImages
          }
        });

        this.$emit('updated');
      } catch (error) {
        console.error('Error adding image:', error);
        alert('Failed to add image. Please try again.');
      } finally {
        this.uploadingImage = false;
      }
    },

    async removeImage(deletedKey) {
      this.uploadingImage = true;

      try {
        const updatedImages = (this.metadata.uploaded_images || []).filter(
          img => img.key !== deletedKey
        );

        await axios.patch(`/api/work/${this.workItem.id}/`, {
          metadata: {
            ...this.metadata,
            uploaded_images: updatedImages
          }
        });

        this.$emit('updated');
      } catch (error) {
        console.error('Error removing image:', error);
        alert('Failed to remove image. Please try again.');
      } finally {
        this.uploadingImage = false;
      }
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
